import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inr, payItem } from "@/lib/razorpay";
import Brand from "@/components/Brand";
import PlansRegister from "@/components/PlansRegister";
import { useAuth } from "@/context/AuthContext";

// Direct lines are not published yet — the owner has not provided numbers.
const WHATSAPP_NUMBER = "";
const PHONE_NUMBER = "";

const REQUIREMENTS = [
  "Website Design (Local Business / Brand)",
  "Full-Stack Web App (Python Backend)",
  "Database Design & Architecture",
  "Ongoing Maintenance & Dedicated Support",
  "Client Portal Access Request",
];

function scrollToContact() {
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
}

function Nav() {
  return (
    <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 py-4" data-testid="main-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid="brand-logo">
          <Brand subtitle="Private Limited" light />
        </a>
        <div className="hidden md:flex items-center space-x-8 text-sm text-slate-600">
          <a href="#services" className="hover:text-cyan-600 transition-colors" data-testid="nav-services">Services</a>
          <a href="#platform" className="hover:text-cyan-600 transition-colors" data-testid="nav-platform">Proprietary Tool</a>
          <a href="#stack" className="hover:text-cyan-600 transition-colors" data-testid="nav-stack">Full-Stack &amp; Python</a>
          <a href="#work" className="hover:text-cyan-600 transition-colors" data-testid="nav-work">Capabilities</a>
          <a href="#reviews" className="hover:text-cyan-600 transition-colors" data-testid="nav-reviews">Client Reviews</a>
        </div>
        <div className="flex items-center space-x-4">
          <Link
            to="/login"
            className="text-xs code-font px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:text-slate-900 hover:border-slate-400 transition-colors"
            data-testid="nav-portal-login"
          >
            Portal Login
          </Link>
          <a
            href="#contact"
            className="text-xs uppercase font-semibold code-font px-4 py-2 rounded bg-cyan-500 text-white hover:bg-cyan-600 transition-colors"
            data-testid="nav-start-project"
          >
            Start Project
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section id="top" className="py-20 lg:py-28 px-6 border-b border-slate-200 relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 hero-anim">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-none" data-testid="hero-headline">
            Built In-House. Delivered Fast.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="hero-subcopy">
            From local web storefronts to scalable Python apps and rock-solid database architectures. We design, code, and deploy directly on our proprietary internal development platform with direct access to our core engineers.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              className="px-6 py-3.5 rounded bg-cyan-500 text-white font-bold text-sm tracking-wide uppercase hover:bg-cyan-600 transition-colors"
              data-testid="hero-request-consultation"
            >
              Request A Consultation
            </a>
            <Link
              to="/login"
              className="px-6 py-3.5 rounded border border-slate-300 bg-white text-slate-700 font-medium text-sm hover:border-cyan-500 hover:text-slate-900 transition-colors code-font"
              data-testid="hero-client-login"
            >
              Client Workspace Login &rarr;
            </Link>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-200 flex flex-wrap gap-6 text-xs text-slate-500 code-font">
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" aria-hidden="true"></span> Direct Engineer Access</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400" aria-hidden="true"></span> Zero Middlemen</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-indigo-400" aria-hidden="true"></span> In-House Infrastructure</span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-xl bg-slate-900/90 p-4 glow-border" data-testid="hero-terminal-frame">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 text-xs code-font text-slate-400">
              <span className="flex items-center gap-1.5" aria-hidden="true">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80"></span>
              </span>
              <span>dreamssoft-platform.internal</span>
              <span className="text-cyan-400">CONNECTED</span>
            </div>
            <div className="mt-4 aspect-[4/3] rounded-lg bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950/40 p-5 flex flex-col justify-between border border-slate-800">
              <div className="space-y-2 code-font text-xs text-slate-400">
                <p className="text-cyan-300 font-semibold">$ dreams-tool generate --stack fullstack-python --db postgres</p>
                <p className="text-slate-500">&gt; compiling component modules...</p>
                <p className="text-slate-400">&gt; schema relational indexing: OK</p>
                <p className="text-emerald-400">&gt; client live sandbox created</p>
              </div>
              <div className="p-3 bg-slate-900/90 rounded border border-cyan-500/30 text-xs text-slate-300 flex justify-between items-center">
                <span>Client Workspace Access</span>
                <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 code-font">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PortalAccess() {
  return (
    <section id="portal" className="py-16 px-6 bg-slate-50 border-b border-slate-200" data-testid="portal-access-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="p-8 rounded-xl bg-white border border-slate-200">
          <div className="text-xs uppercase tracking-widest text-cyan-400 code-font font-bold">Secure Access</div>
          <h2 className="mt-2 text-2xl font-bold text-slate-900">Clients &amp; Team Portal</h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Our proprietary workbench is accessible exclusively to active clients and our internal engineers. Track staging builds, inspect schema migrations, or submit direct change tickets without email latency.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="text-xs font-semibold px-4 py-2 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition-colors code-font"
              data-testid="portal-request-credentials"
            >
              Request Client Login Credentials
            </Link>
          </div>
        </div>
        <div className="p-8 rounded-xl bg-white border border-slate-200 flex flex-col justify-center space-y-4">
          <div className="text-xs text-slate-500 code-font uppercase">Role-Gated Environment</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="client-area-card">
              <div className="text-slate-900 font-semibold text-sm">Client Area</div>
              <p className="text-xs text-slate-500 mt-1">Live sprint previews, feedback pins, design sign-off, direct chat.</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="employee-hub-card">
              <div className="text-slate-900 font-semibold text-sm">Employee Hub</div>
              <p className="text-xs text-slate-500 mt-1">Python pipelines, database migrations, CI deployments.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  {
    id: "service-web",
    title: "Website Design & Build",
    body: "Responsive, conversion-focused websites crafted for local businesses and expanding brands. Clean markup, fast load times, and custom styling engineered on our tool.",
    target: "Target: Local businesses & service brands",
  },
  {
    id: "service-fullstack",
    title: "Full-Stack & Python Applications",
    body: "Robust Python-powered backends, custom REST/GraphQL APIs, user authentication, and interactive web frontends designed for high-concurrency startup workloads.",
    target: "Target: Startups & growing SaaS ventures",
  },
  {
    id: "service-db",
    title: "Database Design & Maintenance",
    body: "Normalized relational database schemas, query optimization, indexing strategies, automated backups, and ongoing system maintenance contracts.",
    target: "Target: Mid-sized to enterprise operations",
  },
];

function Services() {
  return (
    <section id="services" className="py-24 px-6 border-b border-slate-200 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">End-To-End Delivery</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Our Core Engineering Services</h2>
          <p className="mt-3 text-slate-600 text-sm">Every deliverable is crafted in-house to ensure high performance, security, and long-term maintainability.</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {SERVICES.map((s) => (
            <div key={s.id} className="premium-card p-8 rounded-xl bg-slate-50 border border-slate-200 hover:border-cyan-400 transition-colors flex flex-col justify-between" data-testid={s.id}>
              <div>
                <h3 className="mt-2 text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 code-font">{s.target}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Platform() {
  return (
    <section id="platform" className="py-24 px-6 bg-slate-50 border-b border-slate-200" data-testid="platform-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">Proprietary Advantage</span>
          <h2 className="mt-2 text-3xl font-bold text-slate-900 tracking-tight">Why Our In-House Tool Matters For Your Project</h2>
          <p className="mt-4 text-slate-600 text-sm leading-relaxed">
            Unlike agencies that juggle dozens of bloated plugins and disjointed page-builder subscriptions, we build everything through our dedicated internal engine. This gives you:
          </p>
          <ul className="mt-8 space-y-4 text-sm text-slate-700">
            <li className="flex items-start gap-3">
              <span className="mt-1 text-cyan-400 font-bold" aria-hidden="true">&diams;</span>
              <div><strong className="text-slate-900">Faster Delivery:</strong> Pre-built internal modules and scaffolding cut weeks off traditional delivery cycles.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 text-cyan-400 font-bold" aria-hidden="true">&diams;</span>
              <div><strong className="text-slate-900">Affordable Pricing:</strong> Zero third-party recurring builder fees passed down to your invoice.</div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 text-cyan-400 font-bold" aria-hidden="true">&diams;</span>
              <div><strong className="text-slate-900">Direct Engineer Support:</strong> You speak directly with the team writing the code and managing the database.</div>
            </li>
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="architecture-panel">
          <div className="text-xs font-semibold code-font text-slate-500 mb-4 pb-2 border-b border-slate-200 flex justify-between">
            <span>ARCHITECTURAL WORKFLOW</span>
            <span className="text-cyan-600">IN-HOUSE PIPELINE</span>
          </div>
          <div className="aspect-[16/10] rounded-lg bg-gradient-to-tr from-slate-950 via-slate-900 to-cyan-950/30 p-6 flex flex-col justify-around border border-slate-800/80">
            <div className="flex items-center justify-between text-xs code-font">
              <span className="px-3 py-1.5 rounded bg-slate-800 text-slate-200 border border-slate-700">UI / Frontend Wire</span>
              <span className="text-slate-600">&rarr;</span>
              <span className="px-3 py-1.5 rounded bg-cyan-950 text-cyan-300 border border-cyan-700/50">Internal Compiler</span>
              <span className="text-slate-600">&rarr;</span>
              <span className="px-3 py-1.5 rounded bg-slate-800 text-slate-200 border border-slate-700">Python + DB Runtime</span>
            </div>
            <div className="p-3 bg-slate-950/80 rounded border border-slate-800 text-xs text-slate-400">
              <span className="text-emerald-400 code-font">STATUS:</span> Single repository control, real-time client revision preview enabled.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const STACK = [
  { id: "stack-backend", label: "Backend Core", title: "Python Frameworks", body: "FastAPI, Django, and Flask setups configured for high-throughput APIs, authentication, and background workers." },
  { id: "stack-data", label: "Data Layer", title: "Relational DB Design", body: "PostgreSQL and MySQL modeling, ACID transaction design, query indexing, and automated schema migration systems." },
  { id: "stack-frontend", label: "Frontend Delivery", title: "Web Experience", body: "Responsive user interfaces built for lightning-quick loading times across all modern devices and screens." },
  { id: "stack-lifecycle", label: "Lifecycle", title: "System Maintenance", body: "Continuous health monitoring, security patch deployments, database backups, and direct engineer troubleshooting." },
];

function Stack() {
  return (
    <section id="stack" className="py-24 px-6 bg-white border-b border-slate-200" data-testid="stack-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">Technical Capabilities</span>
          <h2 className="mt-2 text-3xl font-bold text-slate-900 tracking-tight">Python Architecture &amp; Database Engineering</h2>
          <p className="mt-3 text-slate-600 text-sm">We handle high-concurrency systems, complex business logic, and structured database foundations.</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STACK.map((s) => (
            <div key={s.id} className="p-6 rounded-lg bg-slate-50 border border-slate-200" data-testid={s.id}>
              <div className="text-cyan-600 text-xs code-font font-bold uppercase">{s.label}</div>
              <h3 className="mt-2 text-lg font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TIERS = [
  { id: "tier-local", label: "Tier 1", title: "Small Local Businesses", body: "Fast, affordable website launches that showcase your local services and get customers calling.", cta: "Consult on Local Site" },
  { id: "tier-startup", label: "Tier 2", title: "High-Growth Startups", body: "Full-stack web applications with Python backends and custom features built rapidly on our internal stack.", cta: "Discuss Startup Scope" },
  { id: "tier-enterprise", label: "Tier 3", title: "Established Enterprises", body: "Database architecture audits, Python backend refactoring, data pipelines, and scheduled system maintenance.", cta: "Book Architecture Review" },
];

function Tiers() {
  return (
    <section id="work" className="py-24 px-6 bg-slate-50 border-b border-slate-200" data-testid="tiers-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">Tailored Engagement</span>
          <h2 className="mt-2 text-3xl font-bold text-slate-900 tracking-tight">Solutions Built For Your Stage</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {TIERS.map((t) => (
            <div key={t.id} className="premium-card p-6 rounded-xl bg-white border border-slate-200" data-testid={t.id}>
              <div className="text-xs font-bold text-cyan-600 uppercase code-font">{t.label}</div>
              <h3 className="mt-2 text-xl font-bold text-slate-900">{t.title}</h3>
              <p className="mt-3 text-sm text-slate-600">{t.body}</p>
              <div className="mt-6 pt-4 border-t border-slate-200">
                <a href="#contact" className="text-xs text-cyan-600 hover:text-cyan-700 font-semibold code-font" data-testid={`${t.id}-cta`}>{t.cta} &rarr;</a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section id="reviews" className="py-24 px-6 bg-white border-b border-slate-200" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">Feedback &amp; Reputation</span>
          <h2 className="mt-2 text-3xl font-bold text-slate-900 tracking-tight">Client Feedback</h2>
          <p className="mt-2 text-sm text-slate-600">Direct feedback from businesses and partners who build with us.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex flex-col justify-between" data-testid="testimonial-placeholder-1">
            <div className="text-xs text-slate-500 code-font">Sample — awaiting verified client stories</div>
            <p className="mt-4 text-sm text-slate-500 italic">Client reviews will be published here as projects are delivered and signed off.</p>
            <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500">
              <span className="text-slate-600 font-semibold">Your business could be first</span>
            </div>
          </div>
          <div className="p-6 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex flex-col justify-between" data-testid="testimonial-placeholder-2">
            <div className="text-xs text-slate-500 code-font">Sample — awaiting verified client stories</div>
            <p className="mt-4 text-sm text-slate-500 italic">Real feedback from live builds appears in this space once clients approve it for publication.</p>
            <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-500">
              <span className="text-slate-600 font-semibold">Verified stories only</span>
            </div>
          </div>
          <div className="p-6 rounded-lg bg-cyan-50 border border-dashed border-cyan-300 flex flex-col justify-center items-center text-center" data-testid="reviews-cta-card">
            <div className="text-xs text-cyan-600 code-font uppercase">Your Project Here</div>
            <p className="mt-2 text-xs text-slate-500">Launch your site or app with our in-house engineering team.</p>
            <a href="#contact" className="mt-4 text-xs font-semibold px-4 py-2 rounded bg-cyan-500 text-white border border-cyan-500 hover:bg-cyan-600 transition-colors code-font" data-testid="reviews-start-project">
              Start Your Project
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BookingDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", contact: "", date: "", time_slot: "Morning (9 AM – 12 PM)", notes: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/bookings", form);
      toast.success("Consultation slot requested — we'll confirm by reply.");
      onOpenChange(false);
      setForm({ name: "", contact: "", date: "", time_slot: "Morning (9 AM – 12 PM)", notes: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-cyan-500 text-sm";
  const labelCls = "block text-xs uppercase font-semibold text-slate-500 code-font mb-2";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 sm:max-w-md" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Book a Free Consultation</DialogTitle>
          <DialogDescription className="text-slate-500 text-sm">
            Pick a preferred day and time window. An engineer confirms directly.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls} htmlFor="booking-name">Your Name</label>
            <input id="booking-name" data-testid="booking-name-input" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Morgan" />
          </div>
          <div>
            <label className={labelCls} htmlFor="booking-contact">Email or Phone</label>
            <input id="booking-contact" data-testid="booking-contact-input" required className={inputCls} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="alex@company.com" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="booking-date">Preferred Day</label>
              <input id="booking-date" data-testid="booking-date-input" required type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls} htmlFor="booking-slot">Time Window</label>
              <select id="booking-slot" data-testid="booking-slot-select" className={inputCls} value={form.time_slot} onChange={(e) => setForm({ ...form, time_slot: e.target.value })}>
                <option>Morning (9 AM – 12 PM)</option>
                <option>Afternoon (12 PM – 4 PM)</option>
                <option>Evening (4 PM – 7 PM)</option>
              </select>
            </div>
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full px-6 py-3 rounded bg-cyan-500 text-white font-bold uppercase text-xs tracking-wider hover:bg-cyan-600 transition-colors disabled:opacity-60"
            data-testid="booking-submit-button"
          >
            {sending ? "Booking…" : "Confirm Slot Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Contact({ onBookSlot }) {
  const [form, setForm] = useState({ name: "", contact: "", requirement: REQUIREMENTS[0], overview: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Project brief received — direct reply in under 24 hours.");
      setForm({ name: "", contact: "", requirement: REQUIREMENTS[0], overview: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = () => {
    if (WHATSAPP_NUMBER) {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}`, "_blank", "noopener");
    } else {
      toast.info("Our direct WhatsApp line goes live soon — send the brief and we reply within 24 hours.");
      scrollToContact();
    }
  };

  const inputCls = "w-full px-4 py-3 rounded bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-cyan-500 text-sm";
  const labelCls = "block text-xs uppercase font-semibold text-slate-500 code-font mb-2";

  return (
    <section id="contact" className="py-24 px-6 bg-slate-50 border-b border-slate-200" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">Get In Touch</span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Let's Discuss Your Project</h2>
          <p className="mt-4 text-slate-600 text-sm leading-relaxed">
            Whether you need a quick-launch local website, an end-to-end full-stack Python application, or complex database design, we deliver fast with zero middleman overhead.
          </p>
          <div className="mt-8 space-y-4">
            <div className="p-4 rounded-lg bg-white border border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 code-font uppercase">Direct Consultation</div>
                <div className="text-sm font-semibold text-slate-900">Free Project Feasibility Call</div>
              </div>
              <button
                type="button"
                onClick={onBookSlot}
                className="text-xs px-3 py-1.5 rounded bg-cyan-500/10 text-cyan-600 border border-cyan-500/30 hover:bg-cyan-500/20 code-font transition-colors"
                data-testid="book-slot-button"
              >
                Book Slot
              </button>
            </div>
            <div className="p-4 rounded-lg bg-white border border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 code-font uppercase">Fast Messaging</div>
                <div className="text-sm font-semibold text-slate-900">WhatsApp &amp; Direct Phone Support</div>
              </div>
              <button
                type="button"
                onClick={handleWhatsApp}
                className="text-xs px-3 py-1.5 rounded bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 hover:bg-emerald-500/20 code-font transition-colors"
                data-testid="whatsapp-button"
              >
                Chat on WhatsApp
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="p-8 rounded-xl bg-white border border-slate-200 shadow-xl space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls} htmlFor="contact-name">Your Name</label>
                <input id="contact-name" data-testid="contact-name-input" required type="text" placeholder="Alex Morgan" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="contact-detail">Work Email or Phone</label>
                <input id="contact-detail" data-testid="contact-detail-input" required type="text" placeholder="alex@company.com" className={inputCls} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="contact-requirement">Project Requirement</label>
              <select id="contact-requirement" data-testid="contact-requirement-select" className={inputCls} value={form.requirement} onChange={(e) => setForm({ ...form, requirement: e.target.value })}>
                {REQUIREMENTS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="contact-overview">Project Overview</label>
              <textarea id="contact-overview" data-testid="contact-overview-input" required rows="4" placeholder="Tell us about what you want built, timeline, and goals..." className={inputCls} value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })}></textarea>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="text-xs text-slate-500">We never share your details &bull; Direct reply in &lt; 24h</p>
              <button
                type="submit"
                disabled={sending}
                className="w-full sm:w-auto px-8 py-3.5 rounded bg-cyan-500 text-white font-bold uppercase text-xs tracking-wider hover:bg-cyan-600 transition-colors disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {sending ? "Submitting…" : "Submit Project Brief"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-12 px-6 bg-slate-50 border-t border-slate-200 text-xs text-slate-500" data-testid="footer">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center space-x-3">
          <img src="/logo.jpg" alt="Dreams Soft Solutions logo" className="h-7 w-7 rounded object-cover bg-white" />
          <span className="text-slate-800 font-bold code-font">Dreams Soft Solutions Pvt. Ltd.</span>
          <span>&mdash; In-House Web &amp; Python Engineering</span>
        </div>
        <div className="flex items-center space-x-6 code-font">
          <a href="#services" className="hover:text-cyan-600 transition-colors" data-testid="footer-services">Services</a>
          <a href="#platform" className="hover:text-cyan-600 transition-colors" data-testid="footer-platform">Proprietary Tool</a>
          <a href="#stack" className="hover:text-cyan-600 transition-colors" data-testid="footer-stack">Tech Specs</a>
          <Link to="/login" className="hover:text-cyan-600 transition-colors" data-testid="footer-client-login">Client Login</Link>
          <a href="#contact" className="hover:text-cyan-600 transition-colors" data-testid="footer-contact">Contact</a>
        </div>
      </div>
    </footer>
  );
}

function Packages() {
  const [packages, setPackages] = useState([]);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  if (!packages.length) return null;

  const pay = async (pkg) => {
    setPaying(pkg.id);
    try {
      await payItem(pkg, {
        onPaid: () => toast.success("Payment received — thank you! Our team will contact you shortly."),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : e.message);
    } finally {
      setPaying(null);
    }
  };

  return (
    <section id="packages" className="py-24 px-6 bg-white border-b border-slate-200" data-testid="packages-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <span className="text-xs uppercase font-bold tracking-widest text-cyan-600 code-font">Pay Online</span>
          <h2 className="mt-2 text-3xl font-bold text-slate-900 tracking-tight">Packages &amp; Pricing</h2>
          <p className="mt-3 text-sm text-slate-600">Fixed-price packages from our team — pay securely online with Razorpay (UPI, cards, netbanking).</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div key={pkg.id} className="premium-card p-6 rounded-xl bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all" data-testid={`package-card-${pkg.id}`}>
              <h3 className="text-lg font-bold text-slate-900">{pkg.title}</h3>
              {pkg.description && <p className="mt-2 text-sm text-slate-600">{pkg.description}</p>}
              <div className="mt-4 text-2xl font-bold text-cyan-700" data-testid={`package-price-${pkg.id}`}>{inr(pkg.amount)}</div>
              <button
                onClick={() => pay(pkg)}
                disabled={paying === pkg.id}
                className="mt-5 w-full px-4 py-2.5 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 transition-colors disabled:opacity-60"
                data-testid={`package-pay-${pkg.id}`}
              >
                {paying === pkg.id ? "Opening checkout…" : `Pay ${inr(pkg.amount)}`}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Already-signed-in members (any role) go straight to their Work Desk
  // instead of seeing "Portal Login" again.
  useEffect(() => {
    if (user && typeof user === "object") navigate("/workdesk", { replace: true });
  }, [user, navigate]);

  return (
    <div className="bg-white text-slate-900 min-h-screen antialiased selection:bg-cyan-200 selection:text-slate-900" data-testid="landing-page">
      <Nav />
      <Hero />
      <PortalAccess />
      <Services />
      <Platform />
      <Stack />
      <Tiers />
      <PlansRegister />
      <Packages />
      <Reviews />
      <Contact onBookSlot={() => setBookingOpen(true)} />
      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
}
